import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getApiUrl } from "../../api";

// 자동등록방지(캡차) 문자 집합 — 혼동되는 0/O/1/I/L 제외
const CAPTCHA_CHARS = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
const genCaptchaText = (len = 5): string =>
  Array.from({ length: len }, () => CAPTCHA_CHARS[Math.floor(Math.random() * CAPTCHA_CHARS.length)]).join("");

const AFFILIATION_TYPE_OPTIONS = [
  { value: "elementary", label: "초등학생" },
  { value: "middle", label: "중학생" },
  { value: "high", label: "고등학생" },
  { value: "university", label: "대학생" },
  { value: "out_of_school", label: "학교 밖 청소년" },
  { value: "other", label: "기타" },
  { value: "unknown", label: "해당 없음" },
];

type FieldErrors = {
  loginId?: string;
  password?: string;
  passwordConfirm?: string;
  name?: string;
  phone?: string;
  email?: string;
  emailVerification?: string;
  wishBook?: string;
  affiliationName?: string;
  affiliationType?: string;
  grade?: string;
  terms?: string;
  captcha?: string;
  general?: string;
};

export default function Register() {
  const navigate = useNavigate();

  // 폼 값
  const [loginId, setLoginId] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [emailVerification, setEmailVerification] = useState("");
  const [affiliationName, setAffiliationName] = useState("");
  const [affiliationType, setAffiliationType] = useState("");
  const [grade, setGrade] = useState("");
  const [gradeMsg, setGradeMsg] = useState("");
  const [wishBook, setWishBook] = useState("");

  // 상태
  const [errors, setErrors] = useState<FieldErrors>({});
  const [loading, setLoading] = useState(false);
  const [loginIdChecked, setLoginIdChecked] = useState<boolean | null>(null); // null: 미확인, true: 사용가능, false: 불가
  const [loginIdCheckMsg, setLoginIdCheckMsg] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [emailVerifying, setEmailVerifying] = useState(false);
  const [success, setSuccess] = useState(false);
  const [validationModalMsg, setValidationModalMsg] = useState("")

  // 약관 동의
  const [agreeTerms, setAgreeTerms] = useState(false);       // [필수] 이용약관
  const [agreePrivacy, setAgreePrivacy] = useState(false);   // [필수] 개인정보 수집·이용
  const agreeAll = agreeTerms && agreePrivacy;
  const handleAgreeAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setAgreeTerms(checked);
    setAgreePrivacy(checked);
    setErrors(prev => { const next = { ...prev }; delete next.terms; return next; });
  };

  // 약관 본문 (consent_policy 테이블에서 active=1, 최신 버전 조회)
  const [termsPolicy, setTermsPolicy] = useState<{ title: string; content: string } | null>(null);
  const [privacyPolicy, setPrivacyPolicy] = useState<{ title: string; content: string } | null>(null);
  const [policyModal, setPolicyModal] = useState<{ title: string; content: string } | null>(null);

  useEffect(() => {
    const load = async (
      type: string,
      setter: React.Dispatch<React.SetStateAction<{ title: string; content: string } | null>>,
    ) => {
      try {
        const res = await fetch(getApiUrl(`/marubit/api/consent-policy/detail?consent_type=${type}`));
        if (res.ok) {
          const d = await res.json();
          setter({ title: d.title, content: d.content });
        }
      } catch {
        /* 약관 조회 실패 시 기본 라벨만 표시 */
      }
    };
    load("terms", setTermsPolicy);
    load("privacy", setPrivacyPolicy);
  }, []);

  // 자동등록방지(캡차)
  const [captchaText, setCaptchaText] = useState<string>(genCaptchaText);
  const [captchaInput, setCaptchaInput] = useState("");
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const drawCaptcha = (text: string) => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;
    const W = canvas.width;
    const H = canvas.height;
    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = "#f5f2f0";
    ctx.fillRect(0, 0, W, H);
    // 노이즈 선
    for (let i = 0; i < 6; i++) {
      ctx.strokeStyle = `rgba(136,114,103,${Math.random() * 0.4 + 0.15})`;
      ctx.beginPath();
      ctx.moveTo(Math.random() * W, Math.random() * H);
      ctx.lineTo(Math.random() * W, Math.random() * H);
      ctx.stroke();
    }
    // 글자
    const step = W / (text.length + 1);
    for (let i = 0; i < text.length; i++) {
      ctx.save();
      ctx.translate(step * (i + 0.8), H / 2 + (Math.random() * 8 - 4));
      ctx.rotate(((Math.random() * 40 - 20) * Math.PI) / 180);
      ctx.font = `bold ${22 + Math.floor(Math.random() * 6)}px monospace`;
      ctx.fillStyle = `hsl(${20 + Math.floor(Math.random() * 30)}, 35%, ${25 + Math.floor(Math.random() * 18)}%)`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(text[i], 0, 0);
      ctx.restore();
    }
    // 노이즈 점
    for (let i = 0; i < 28; i++) {
      ctx.fillStyle = `rgba(136,114,103,${Math.random() * 0.5})`;
      ctx.beginPath();
      ctx.arc(Math.random() * W, Math.random() * H, Math.random() * 1.5, 0, Math.PI * 2);
      ctx.fill();
    }
  };

  useEffect(() => {
    drawCaptcha(captchaText);
  }, [captchaText]);

  const refreshCaptcha = () => {
    setCaptchaText(genCaptchaText());
    setCaptchaInput("");
    setErrors(prev => { const next = { ...prev }; delete next.captcha; return next; });
  };

  const setError = (field: keyof FieldErrors, msg: string) =>
    setErrors(prev => ({ ...prev, [field]: msg }));
  const clearError = (field: keyof FieldErrors) =>
    setErrors(prev => { const next = { ...prev }; delete next[field]; return next; });

  const inputStyle = (hasError: boolean): React.CSSProperties => ({
    borderColor: hasError ? "#ef4444" : "#e5e7eb",
  });

  // 아이디 중복 확인
  const handleCheckLoginId = async () => {
    clearError("loginId");
    setLoginIdChecked(null);
    setLoginIdCheckMsg("");

    if (!loginId.trim()) {
      setError("loginId", "아이디를 입력해주세요.");
      return;
    }

    try {
      const res = await fetch(getApiUrl(`/marubit/api/member/check_id?login_id=${encodeURIComponent(loginId)}`));
      const data = await res.json();

      if (res.ok) {
        setLoginIdChecked(true);
        setLoginIdCheckMsg("사용 가능한 아이디입니다.");
      } else {
        setLoginIdChecked(false);
        setLoginIdCheckMsg(data.detail || "사용할 수 없는 아이디입니다.");
        setError("loginId", data.detail || "사용할 수 없는 아이디입니다.");
      }
    } catch (err) {
      setError("loginId", "서버 연결 오류: " + (err as Error).message);
    }
  };

  // 아이디 변경 시 중복확인 초기화
  const handleLoginIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLoginId(e.target.value);
    setLoginIdChecked(null);
    setLoginIdCheckMsg("");
    clearError("loginId");
  };

  // 이메일 인증번호 발송
  const handleSendEmailVerification = async () => {
    clearError("email");
    if (!email.trim()) {
      setError("email", "이메일을 입력해주세요.");
      return;
    }

    setEmailSending(true);
    try {
      const res = await fetch(getApiUrl("/marubit/api/member/email-verification"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = await res.json();

      if (res.ok) {
        setEmailSent(true);
        setEmailVerified(false);
        setEmailVerification("");
        clearError("email");
      } else {
        setError("email", data.detail || "인증번호 발송에 실패했습니다.");
      }
    } catch (err) {
      setError("email", "서버 연결 오류: " + (err as Error).message);
    }
    setEmailSending(false);
  };

  // 이메일 인증번호 확인
  const handleVerifyEmail = async () => {
    clearError("emailVerification");
    if (!emailVerification.trim()) {
      setError("emailVerification", "인증번호를 입력해주세요.");
      return;
    }

    setEmailVerifying(true);
    try {
      const res = await fetch(getApiUrl("/marubit/api/member/email-verification/verify"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), email_verification: emailVerification.trim() }),
      });
      const data = await res.json();

      if (res.ok && data.verified) {
        setEmailVerified(true);
        clearError("emailVerification");
      } else {
        setError("emailVerification", data.detail || "인증번호가 올바르지 않습니다.");
      }
    } catch (err) {
      setError("emailVerification", "서버 연결 오류: " + (err as Error).message);
    }
    setEmailVerifying(false);
  };

  // 회원가입 제출
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const newErrors: FieldErrors = {};

     console.log({ loginId, loginIdChecked, password, passwordConfirm, name, phone, email, emailVerified, wishBook, affiliationName, affiliationType });

    if (!agreeTerms || !agreePrivacy) newErrors.terms = "필수 약관에 동의해주세요.";

    if (!loginId.trim()) newErrors.loginId = "아이디를 입력해주세요.";
    else if (loginIdChecked !== true) newErrors.loginId = "아이디 중복 확인을 해주세요.";

    if (!password) newErrors.password = "비밀번호를 입력해주세요.";
    if (!passwordConfirm) newErrors.passwordConfirm = "비밀번호 확인을 입력해주세요.";
    else if (password !== passwordConfirm) newErrors.passwordConfirm = "비밀번호가 일치하지 않습니다.";

    if (!name.trim()) newErrors.name = "이름을 입력해주세요.";
    if (!phone.trim()) newErrors.phone = "휴대폰 번호를 입력해주세요.";
    if (!email.trim()) newErrors.email = "이메일을 입력해주세요.";
    else if (!emailVerified) newErrors.email = "이메일 인증을 완료해주세요.";
    if (!wishBook.trim()) newErrors.wishBook = "선물받고싶은 희망 도서를 입력해주세요. 마루빛에 열심히 방문하면 희망도서를 선물로 드려요. 회원가입 후 변경 가능해요";
    if (!wishBook.trim()) newErrors.wishBook = "희망 도서를 입력해주세요.";
    if (!affiliationName.trim()) newErrors.affiliationName = "소속명을 입력해주세요.";
    if (!affiliationType) newErrors.affiliationType = "소속 유형을 선택해주세요.";

    if (!captchaInput.trim()) newErrors.captcha = "자동등록방지 문자를 입력해주세요.";
    else if (captchaInput.trim().toUpperCase() !== captchaText)
      newErrors.captcha = "자동등록방지 문자가 일치하지 않습니다. 다시 시도해주세요.";

    if (Object.keys(newErrors).length > 0) {
    setErrors(newErrors);
    const firstMsg = Object.values(newErrors)[0];
    setValidationModalMsg(firstMsg ?? "입력 정보를 확인해주세요.");
    // 캡차 오류 시 새 문자 발급(에러 메시지는 유지)
    if (newErrors.captcha) { setCaptchaText(genCaptchaText()); setCaptchaInput(""); }
    return;
    }

    setLoading(true);
    try {
      const body: Record<string, string | null> = {
        login_id: loginId.trim(),
        password,
        name: name.trim(),
        phone: phone.trim(),
        email: email.trim(),
        wish_book: wishBook.trim(),
        affiliation_name: affiliationName.trim() || null,
        affiliation_type: affiliationType || null,
        grade: grade.trim() || null,
      };

      const res = await fetch(getApiUrl("/marubit/api/member/signup"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const data = await res.json();

      if (res.ok) {
        setSuccess(true);
      } else {
        // 필드별 에러 매핑
        const detail = data.detail || "회원가입 중 오류가 발생했습니다.";
        if (typeof detail === "string") {
          if (detail.includes("아이디")) setErrors({ loginId: detail });
          else if (detail.includes("이메일")) setErrors({ email: detail });
          else if (detail.includes("비밀번호")) setErrors({ password: detail });
          else setErrors({ general: detail });
        } else if (Array.isArray(detail)) {
          // Pydantic validation error
          const fieldMap: FieldErrors = {};
          detail.forEach((err: { loc: string[]; msg: string }) => {
            const field = err.loc?.[1];
            if (field === "login_id") fieldMap.loginId = err.msg;
            else if (field === "password") fieldMap.password = err.msg;
            else if (field === "phone") fieldMap.phone = err.msg;
            else if (field === "email") fieldMap.email = err.msg;
            else if (field === "wish_book") fieldMap.wishBook = err.msg;
          });
          setErrors(fieldMap);
        } else {
          setErrors({ general: "회원가입 중 오류가 발생했습니다." });
        }
      }
    } catch (err) {
      setErrors({ general: "서버 연결 오류: " + (err as Error).message });
    }
    setLoading(false);
  };

  // ─── 성공 화면 ───
  if (success) {
    return (
      <div className="min-h-[calc(100vh-200px)] flex items-center justify-center bg-[#f5f2f0]">
        <div className="w-full max-w-[420px] bg-white rounded-2xl shadow-[0_8px_32px_rgba(136,114,103,0.1)] px-9 py-10 text-center">
          <div className="w-14 h-14 bg-[#f0ebe8] rounded-full flex items-center justify-center mx-auto mb-5">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
              <path d="M20 6L9 17L4 12" stroke="#887267" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <h2 className="text-[20px] font-bold text-[#3d2f2a] mb-2">회원가입이 완료되었습니다</h2>
          <p className="text-[13px] text-[#999] mb-7 leading-relaxed">
            관리자 승인 후 로그인이 가능합니다.<br />
            승인까지 잠시 기다려주세요.
          </p>
          <button
            onClick={() => navigate("/auth/login")}
            className="w-full h-[46px] bg-[#887267] text-white border-none rounded-lg text-[15px] font-bold cursor-pointer hover:bg-[#6b5248] transition-colors duration-150"
          >
            로그인 화면으로
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="min-h-[calc(100vh-200px)] flex items-center justify-center bg-[#f5f2f0] py-10">
      <div className="w-full max-w-[1000px] bg-white rounded-2xl shadow-[0_8px_32px_rgba(136,114,103,0.1)] px-12 py-10">

        {/* 헤더 */}
        <div className="mb-7">
          <h2 className="text-[20px] font-bold text-[#3d2f2a] mb-1">회원가입</h2>
          <p className="text-[13px] text-[#999]">마루빛 서비스 이용을 위한 회원 정보를 입력해주세요.</p>
        </div>

        {errors.general && (
          <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-[13px] text-red-600">
            {errors.general}
          </div>
        )}

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">

          {/* ── 약관 동의 섹션 ── */}
          <div className="border border-[#f0ebe8] rounded-xl p-4">
            <label className="flex items-center gap-2 cursor-pointer pb-3 mb-3 border-b border-[#f0ebe8]">
              <input
                type="checkbox"
                checked={agreeAll}
                onChange={handleAgreeAll}
                className="w-[16px] h-[16px] cursor-pointer accent-[#887267]"
              />
              <span className="text-[14px] font-bold text-[#3d2f2a]">약관 전체 동의</span>
            </label>
            <div className="flex flex-col gap-[10px]">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="agreeTerms"
                  checked={agreeTerms}
                  onChange={e => { setAgreeTerms(e.target.checked); clearError("terms"); }}
                  className="w-[15px] h-[15px] cursor-pointer accent-[#887267] flex-shrink-0"
                />
                <span className="text-[13px] text-[#555]">
                  <span className="text-red-400 font-semibold">[필수]</span>{" "}
                  <button
                    type="button"
                    onClick={() => termsPolicy && setPolicyModal(termsPolicy)}
                    className="text-[#5a4038] underline underline-offset-2 hover:text-[#887267]"
                  >
                    {termsPolicy?.title ?? "이용약관"}
                  </button>{" "}
                  동의
                </span>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="agreePrivacy"
                  checked={agreePrivacy}
                  onChange={e => { setAgreePrivacy(e.target.checked); clearError("terms"); }}
                  className="w-[15px] h-[15px] cursor-pointer accent-[#887267] flex-shrink-0"
                />
                <span className="text-[13px] text-[#555]">
                  <span className="text-red-400 font-semibold">[필수]</span>{" "}
                  <button
                    type="button"
                    onClick={() => privacyPolicy && setPolicyModal(privacyPolicy)}
                    className="text-[#5a4038] underline underline-offset-2 hover:text-[#887267]"
                  >
                    {privacyPolicy?.title ?? "개인정보 수집·이용 동의"}
                  </button>
                </span>
              </div>
            </div>
            {errors.terms && <span className="text-[12px] text-red-500 mt-2 block">{errors.terms}</span>}
          </div>

          {/* ── 필수 정보 섹션 ── */}
          <p className="text-[11px] font-bold text-[#887267] tracking-widest uppercase mt-1">필수 정보</p>

          {/* 아이디 */}
          <div>
            <label className="block text-[13px] font-semibold text-[#887267] mb-[6px]">아이디 <span className="text-red-400">*</span></label>
            <div className="flex gap-2">
              <input
                type="text"
                value={loginId}
                onChange={handleLoginIdChange}
                placeholder="6~12자 영문+숫자"
                className="flex-1 h-11 px-[14px] border-[1.5px] rounded-lg text-[14px] outline-none box-border transition-colors duration-150"
                style={inputStyle(!!errors.loginId)}
                onFocus={e => (e.currentTarget.style.borderColor = "#887267")}
                onBlur={e => (e.currentTarget.style.borderColor = errors.loginId ? "#ef4444" : "#e5e7eb")}
              />
              <button
                type="button"
                onClick={handleCheckLoginId}
                className="h-11 px-4 bg-[#887267] text-white rounded-lg text-[13px] font-bold hover:bg-[#6b5248] transition-colors duration-150 whitespace-nowrap flex-shrink-0"
              >
                중복확인
              </button>
            </div>
            {errors.loginId && <span className="text-[12px] text-red-500 mt-1 block">{errors.loginId}</span>}
            {!errors.loginId && loginIdChecked === true && (
              <span className="text-[12px] text-green-600 mt-1 block">✓ {loginIdCheckMsg}</span>
            )}
          </div>

          {/* 비밀번호 */}
          <div>
            <label className="block text-[13px] font-semibold text-[#887267] mb-[6px]">비밀번호 <span className="text-red-400">*</span></label>
            <input
              type="password"
              value={password}
              onChange={e => { setPassword(e.target.value); clearError("password"); }}
              placeholder="8~16자, 영문+숫자+특수문자 포함"
              className="w-full h-11 px-[14px] border-[1.5px] rounded-lg text-[14px] outline-none box-border transition-colors duration-150"
              style={inputStyle(!!errors.password)}
              onFocus={e => (e.currentTarget.style.borderColor = "#887267")}
              onBlur={e => (e.currentTarget.style.borderColor = errors.password ? "#ef4444" : "#e5e7eb")}
            />
            {errors.password && <span className="text-[12px] text-red-500 mt-1 block">{errors.password}</span>}
          </div>

          {/* 비밀번호 확인 */}
          <div>
            <label className="block text-[13px] font-semibold text-[#887267] mb-[6px]">비밀번호 확인 <span className="text-red-400">*</span></label>
            <input
              type="password"
              value={passwordConfirm}
              onChange={e => {
                const val = e.target.value;
                setPasswordConfirm(val);
                if (val && password !== val) {
                    setError("passwordConfirm", "비밀번호가 일치하지 않습니다.");
                } else {
                    clearError("passwordConfirm");
                }
              }}
              placeholder="비밀번호를 다시 입력하세요"
              className="w-full h-11 px-[14px] border-[1.5px] rounded-lg text-[14px] outline-none box-border transition-colors duration-150"
              style={inputStyle(!!errors.passwordConfirm)}
              onFocus={e => (e.currentTarget.style.borderColor = "#887267")}
              onBlur={e => (e.currentTarget.style.borderColor = errors.passwordConfirm ? "#ef4444" : "#e5e7eb")}
            />
            {errors.passwordConfirm && <span className="text-[12px] text-red-500 mt-1 block">{errors.passwordConfirm}</span>}
            {!errors.passwordConfirm && passwordConfirm && password === passwordConfirm && (
              <span className="text-[12px] text-green-600 mt-1 block">✓ 비밀번호가 일치합니다.</span>
            )}
          </div>

          {/* 이름 */}
          <div>
            <label className="block text-[13px] font-semibold text-[#887267] mb-[6px]">이름 <span className="text-red-400">*</span></label>
            <input
              type="text"
              value={name}
              onChange={e => { setName(e.target.value); clearError("name"); }}
              placeholder="이름을 입력하세요"
              className="w-full h-11 px-[14px] border-[1.5px] rounded-lg text-[14px] outline-none box-border transition-colors duration-150"
              style={inputStyle(!!errors.name)}
              onFocus={e => (e.currentTarget.style.borderColor = "#887267")}
              onBlur={e => (e.currentTarget.style.borderColor = errors.name ? "#ef4444" : "#e5e7eb")}
            />
            {errors.name && <span className="text-[12px] text-red-500 mt-1 block">{errors.name}</span>}
          </div>

          {/* 휴대폰 */}
          <div>
            <label className="block text-[13px] font-semibold text-[#887267] mb-[6px]">휴대폰 번호 <span className="text-red-400">*</span></label>
            <input
              type="tel"
              value={phone}
              onChange={e => { setPhone(e.target.value); clearError("phone"); }}
              placeholder="예: 01012345678"
              className="w-full h-11 px-[14px] border-[1.5px] rounded-lg text-[14px] outline-none box-border transition-colors duration-150"
              style={inputStyle(!!errors.phone)}
              onFocus={e => (e.currentTarget.style.borderColor = "#887267")}
              onBlur={e => (e.currentTarget.style.borderColor = errors.phone ? "#ef4444" : "#e5e7eb")}
            />
            {errors.phone && <span className="text-[12px] text-red-500 mt-1 block">{errors.phone}</span>}
          </div>

          {/* 이메일 */}
          <div>
            <label className="block text-[13px] font-semibold text-[#887267] mb-[6px]">이메일 <span className="text-red-400">*</span></label>
            <div className="flex gap-2">
              <input
                type="email"
                value={email}
                onChange={e => {
                  setEmail(e.target.value);
                  setEmailSent(false);
                  setEmailVerified(false);
                  clearError("email");
                  clearError("emailVerification");
                }}
                placeholder="이메일을 입력하세요"
                disabled={emailVerified}
                className="flex-1 h-11 px-[14px] border-[1.5px] rounded-lg text-[14px] outline-none box-border transition-colors duration-150 disabled:bg-gray-50 disabled:text-gray-400"
                style={inputStyle(!!errors.email)}
                onFocus={e => (e.currentTarget.style.borderColor = "#887267")}
                onBlur={e => (e.currentTarget.style.borderColor = errors.email ? "#ef4444" : "#e5e7eb")}
              />
              <button
                type="button"
                onClick={handleSendEmailVerification}
                disabled={emailSending || emailVerified}
                className={[
                  "h-11 px-4 rounded-lg text-[13px] font-bold transition-colors duration-150 whitespace-nowrap flex-shrink-0",
                  emailVerified
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : emailSending
                    ? "bg-[#b8a39b] text-white cursor-not-allowed"
                    : "bg-[#887267] text-white hover:bg-[#6b5248] cursor-pointer",
                ].join(" ")}
              >
                {emailVerified ? "인증완료" : emailSending ? "발송중..." : emailSent ? "재발송" : "인증번호 발송"}
              </button>
            </div>
            {errors.email && <span className="text-[12px] text-red-500 mt-1 block">{errors.email}</span>}
            {emailVerified && <span className="text-[12px] text-green-600 mt-1 block">✓ 이메일 인증이 완료되었습니다.</span>}
          </div>

          {/* 인증번호 입력 */}
          {emailSent && !emailVerified && (
            <div>
              <label className="block text-[13px] font-semibold text-[#887267] mb-[6px]">인증번호 <span className="text-red-400">*</span></label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={emailVerification}
                  onChange={e => { setEmailVerification(e.target.value); clearError("emailVerification"); }}
                  placeholder="6자리 인증번호"
                  maxLength={6}
                  className="flex-1 h-11 px-[14px] border-[1.5px] rounded-lg text-[14px] outline-none box-border transition-colors duration-150 tracking-widest"
                  style={inputStyle(!!errors.emailVerification)}
                  onFocus={e => (e.currentTarget.style.borderColor = "#887267")}
                  onBlur={e => (e.currentTarget.style.borderColor = errors.emailVerification ? "#ef4444" : "#e5e7eb")}
                />
                <button
                  type="button"
                  onClick={handleVerifyEmail}
                  disabled={emailVerifying}
                  className={[
                    "h-11 px-4 rounded-lg text-[13px] font-bold transition-colors duration-150 whitespace-nowrap flex-shrink-0",
                    emailVerifying
                      ? "bg-[#b8a39b] text-white cursor-not-allowed"
                      : "bg-[#887267] text-white hover:bg-[#6b5248] cursor-pointer",
                  ].join(" ")}
                >
                  {emailVerifying ? "확인 중..." : "확인"}
                </button>
              </div>
              {errors.emailVerification && (
                <span className="text-[12px] text-red-500 mt-1 block">{errors.emailVerification}</span>
              )}
              <span className="text-[12px] text-[#aaa] mt-1 block">
                이메일로 발송된 6자리 인증번호를 입력해주세요. (10분 이내 입력)
              </span>
            </div>
          )}

          {/* 희망 도서 */}
          <div>
            <label className="block text-[13px] font-semibold text-[#887267] mb-[6px]">희망 도서 <span className="text-red-400">*</span></label>
            <input
              type="text"
              value={wishBook}
              onChange={e => { setWishBook(e.target.value); clearError("wishBook"); }}
              placeholder="희망하는 도서 제목을 입력하세요"
              className="w-full h-11 px-[14px] border-[1.5px] rounded-lg text-[14px] outline-none box-border transition-colors duration-150"
              style={inputStyle(!!errors.wishBook)}
              onFocus={e => (e.currentTarget.style.borderColor = "#887267")}
              onBlur={e => (e.currentTarget.style.borderColor = errors.wishBook ? "#ef4444" : "#e5e7eb")}
            />
            <p className="text-[12px] text-[#aaa] mt-1 leading-relaxed">
            마루빛에 열심히 방문하면 희망 도서를 선물로 드려요 🎁 가입 후에도 변경 가능합니다.
            </p>
            {errors.wishBook && <span className="text-[12px] text-red-500 mt-1 block">{errors.wishBook}</span>}
          </div>

          {/* ── 선택 정보 섹션 ── */}
          <div className="border-t border-[#f0ebe8] pt-4 mt-1">
            <p className="text-[11px] font-bold text-[#887267] tracking-widest uppercase mb-4">선택 정보</p>

            {/* 소속명 */}
            <div className="mb-4">
              <label className="block text-[13px] font-semibold text-[#887267] mb-[6px]">소속명<span className="text-red-400">*</span></label>
              <input
                type="text"
                value={affiliationName}
                onChange={e => setAffiliationName(e.target.value)}
                placeholder="학교명 또는 소속 기관명"
                className="w-full h-11 px-[14px] border-[1.5px] border-[#e5e7eb] rounded-lg text-[14px] outline-none box-border transition-colors duration-150"
                onFocus={e => (e.currentTarget.style.borderColor = "#887267")}
                onBlur={e => (e.currentTarget.style.borderColor = "#e5e7eb")}
              />
            </div>

            {/* 소속 유형 */}
            <div className="mb-4">
              <label className="block text-[13px] font-semibold text-[#887267] mb-[6px]">소속 유형<span className="text-red-400">*</span></label>
              <select
                value={affiliationType}
                onChange={e => setAffiliationType(e.target.value)}
                className="w-full h-11 px-[14px] border-[1.5px] border-[#e5e7eb] rounded-lg text-[14px] outline-none box-border transition-colors duration-150 bg-white text-[#333]"
                style={{ appearance: "auto" }}
                onFocus={e => (e.currentTarget.style.borderColor = "#887267")}
                onBlur={e => (e.currentTarget.style.borderColor = "#e5e7eb")}
              >
                <option value="">선택 안 함</option>
                {AFFILIATION_TYPE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* 학년 */}
            <div>
              <label className="block text-[13px] font-semibold text-[#887267] mb-[6px]">학년</label>
              <input
                type="text"
                value={grade}
                onChange={e => {
                const val = e.target.value;
                if (val === "" || /^\d+$/.test(val)) {
                    setGrade(val);
                    setGradeMsg("");
                } else {
                    setGradeMsg("숫자만 입력해주세요.");
                }
                }}
                placeholder="숫자만 입력 (예: 1)"
                className="w-full h-11 px-[14px] border-[1.5px] border-[#e5e7eb] rounded-lg text-[14px] outline-none box-border transition-colors duration-150"
                style={{ borderColor: gradeMsg ? "#ef4444" : undefined }}
                onFocus={e => (e.currentTarget.style.borderColor = "#887267")}
                onBlur={e => (e.currentTarget.style.borderColor = gradeMsg ? "#ef4444" : "#e5e7eb")}
              />
            </div>
          </div>

          {/* ── 자동등록방지(캡차) ── */}
          <div className="border-t border-[#f0ebe8] pt-4 mt-1">
            <label className="block text-[13px] font-semibold text-[#887267] mb-[6px]">
              자동등록방지 <span className="text-red-400">*</span>
            </label>
            <div className="flex items-center gap-2 mb-2">
              <canvas
                ref={canvasRef}
                width={150}
                height={48}
                className="rounded-lg border-[1.5px] border-[#e5e7eb] select-none"
              />
              <button
                type="button"
                onClick={refreshCaptcha}
                title="새로고침"
                aria-label="자동등록방지 문자 새로고침"
                className="h-12 px-3 rounded-lg border-[1.5px] border-[#e5e7eb] bg-white text-[#887267] hover:bg-[#f9f6f4] transition-colors duration-150 flex items-center justify-center flex-shrink-0"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  <polyline points="23 4 23 10 17 10" />
                  <polyline points="1 20 1 14 7 14" />
                  <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                </svg>
              </button>
            </div>
            <input
              type="text"
              value={captchaInput}
              onChange={e => { setCaptchaInput(e.target.value); clearError("captcha"); }}
              placeholder="위 이미지의 문자를 입력하세요"
              autoComplete="off"
              className="w-full h-11 px-[14px] border-[1.5px] rounded-lg text-[14px] outline-none box-border transition-colors duration-150 tracking-widest"
              style={inputStyle(!!errors.captcha)}
              onFocus={e => (e.currentTarget.style.borderColor = "#887267")}
              onBlur={e => (e.currentTarget.style.borderColor = errors.captcha ? "#ef4444" : "#e5e7eb")}
            />
            {errors.captcha && <span className="text-[12px] text-red-500 mt-1 block">{errors.captcha}</span>}
          </div>

          {/* 제출 버튼 */}
          <button
            type="submit"
            disabled={loading}
            className={[
              "w-full h-[46px] text-white border-none rounded-lg text-[15px] font-bold transition-colors duration-150 mt-2",
              loading ? "bg-[#b8a39b] cursor-not-allowed" : "bg-[#887267] cursor-pointer hover:bg-[#6b5248]",
            ].join(" ")}
          >
            {loading ? "처리 중..." : "회원가입"}
          </button>

        </form>

        {/* 하단 링크 */}
        <div className="flex justify-center items-center mt-5 gap-1">
          <Link to="/auth/login" className="px-3 text-[13px] text-[#666] no-underline">로그인</Link>
          <span className="text-gray-200">|</span>
          <Link to="/auth/find_id" className="px-3 text-[13px] text-[#666] no-underline">아이디 찾기</Link>
          <span className="text-gray-200">|</span>
          <Link to="/auth/find_pw" className="px-3 text-[13px] text-[#666] no-underline">비밀번호 찾기</Link>
        </div>
      </div>
    </div>

    {/* 약관 내용 모달 */}
    {policyModal && (
      <div
        className="fixed inset-0 flex items-center justify-center bg-black/40 z-50 px-4"
        onClick={() => setPolicyModal(null)}
      >
        <div
          className="bg-white rounded-[14px] shadow-[0_8px_32px_rgba(0,0,0,0.15)] w-full max-w-[560px] max-h-[80vh] flex flex-col"
          onClick={e => e.stopPropagation()}
        >
          <div className="flex items-center justify-between px-6 py-4 border-b border-[#f0ebe8]">
            <h3 className="text-[16px] font-bold text-[#3d2f2a]">{policyModal.title}</h3>
            <button
              type="button"
              onClick={() => setPolicyModal(null)}
              aria-label="닫기"
              className="text-gray-400 hover:text-gray-600"
            >
              <svg viewBox="0 0 24 24" width={22} height={22} fill="none" stroke="currentColor" strokeWidth={2}>
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
          <div className="px-6 py-5 overflow-y-auto text-[13px] text-[#555] leading-relaxed whitespace-pre-wrap">
            {policyModal.content}
          </div>
          <div className="px-6 py-4 border-t border-[#f0ebe8]">
            <button
              type="button"
              onClick={() => setPolicyModal(null)}
              className="w-full h-[42px] bg-[#887267] text-white border-none rounded-lg text-[14px] font-bold cursor-pointer hover:bg-[#6b5248] transition-colors duration-150"
            >
              확인
            </button>
          </div>
        </div>
      </div>
    )}

    {validationModalMsg && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/40 z-50">
            <div className="bg-white rounded-[14px] shadow-[0_8px_32px_rgba(0,0,0,0.15)] px-6 py-7 w-[300px] text-center">
            <p className="text-[14px] text-[#444] mb-5 leading-relaxed">{validationModalMsg}</p>
            <button
                onClick={() => setValidationModalMsg("")}
                className="w-full h-[42px] bg-[#887267] text-white border-none rounded-lg text-[14px] font-bold cursor-pointer hover:bg-[#6b5248] transition-colors duration-150"
            >
                확인
            </button>
            </div>
        </div>
        )}
    </>
  );
}